// API Service for XpressPrint Application
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

class ApiService {
    constructor() {
        this.baseURL = API_BASE_URL;
        this.cache = new Map();
        this.cacheTimeout = 5 * 60 * 1000; // 5 minutes cache
        this.pendingRequests = new Map(); // Track pending requests to prevent duplicates
        // this.requestQueue = new Map(); // Track request timing for rate limiting
        // this.maxRequestsPerMinute = 60; // Rate limit: 60 requests per minute
    }

    getCacheKey(endpoint) {
        return endpoint;
    }

    isValidCache(cacheEntry) {
        return cacheEntry && Date.now() - cacheEntry.timestamp < this.cacheTimeout;
    }

    // Rate limiting helper
    // canMakeRequest(endpoint) {
    //     const now = Date.now();
    //     const minuteAgo = now - 60000; // 1 minute ago
    //     
    //     // Clean old entries
    //     if (this.requestQueue.has(endpoint)) {
    //         const requests = this.requestQueue.get(endpoint).filter(time => time > minuteAgo);
    //         this.requestQueue.set(endpoint, requests);
    //         
    //         // Check if we can make another request
    //         if (requests.length >= this.maxRequestsPerMinute) {
    //             return false;
    //         }
    //     }
    //     
    //     return true;
    // }

    // Record request for rate limiting
    // recordRequest(endpoint) {
    //     const now = Date.now();
    //     if (!this.requestQueue.has(endpoint)) {
    //         this.requestQueue.set(endpoint, []);
    //     }
    //     this.requestQueue.get(endpoint).push(now);
    // }

    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const cacheKey = this.getCacheKey(endpoint);

        // Check cache for GET requests only
        if (!options.method || options.method === "GET") {
            const cached = this.cache.get(cacheKey);
            if (this.isValidCache(cached)) {
                console.log(`Cache hit for: ${endpoint}`);
                return cached.data;
            }

            // Check if there's a pending request for the same endpoint
            if (this.pendingRequests.has(cacheKey)) {
                console.log(`Waiting for pending request: ${endpoint}`);
                return this.pendingRequests.get(cacheKey);
            }

            // Check rate limiting for GET requests
            // if (!this.canMakeRequest(endpoint)) {
            //     console.warn(`Rate limit exceeded for: ${endpoint}. Using cached data or throwing error.`);
            //     // Return cached data if available, even if expired
            //     const expiredCache = this.cache.get(cacheKey);
            //     if (expiredCache) {
            //         console.log(`Using expired cache for: ${endpoint}`);
            //         return expiredCache.data;
            //     }
            //     throw new Error(`Rate limit exceeded for ${endpoint}. Please try again later.`);
            // }
        }

        const config = {
            ...options,
        };

        if (!(options.body instanceof FormData)) {
            config.headers = {
                "Content-Type": "application/json",
                ...options.headers,
            };
        } else {
            config.headers = {
                ...options.headers,
            };
        }

        const requestPromise = (async () => {
            try {
                // Record request for rate limiting
                // this.recordRequest(endpoint);

                const response = await fetch(url, config);

                if (!response.ok) {
                    let errorData;
                    try {
                        const text = await response.text();
                        errorData = text ? JSON.parse(text) : { message: `HTTP error! status: ${response.status}` };
                    } catch (e) {
                        errorData = { message: `HTTP error! status: ${response.status}` };
                    }
                    const error = new Error(errorData.message || errorData.error?.message || `HTTP error! status: ${response.status}`);
                    error.response = {
                        data: errorData,
                        status: response.status,
                        statusText: response.statusText
                    };
                    throw error;
                }

                // Handle 204 No Content responses (common for DELETE operations)
                if (response.status === 204 || response.statusText === 'No Content') {
                    return { status: 'success', data: null, message: 'Operation completed successfully' };
                }

                // Check if response has content to parse
                const contentType = response.headers.get('content-type');
                let data;
                if (contentType && contentType.includes('application/json')) {
                    const text = await response.text();
                    data = text ? JSON.parse(text) : { status: 'success', data: null };
                } else {
                    data = { status: 'success', data: null };
                }

                // Cache successful GET requests
                if (!options.method || options.method === "GET") {
                    this.cache.set(cacheKey, {
                        data,
                        timestamp: Date.now(),
                    });
                    console.log(`Cached response for: ${endpoint}`);
                } else {
                    // Invalidate related cache entries after mutations
                    this.invalidateRelatedCache(endpoint, options.method);
                }

                return data;
            } catch (error) {
                console.error(`API request failed: ${endpoint}`, error);
                throw error;
            } finally {
                // Remove from pending requests
                if (!options.method || options.method === "GET") {
                    this.pendingRequests.delete(cacheKey);
                }
            }
        })();

        // Store the promise for GET requests to prevent duplicates
        if (!options.method || options.method === "GET") {
            this.pendingRequests.set(cacheKey, requestPromise);
        }

        return requestPromise;
    }

    // Product API methods
    async getAllProducts(filters = {}) {
        const queryParams = new URLSearchParams();

        Object.keys(filters).forEach(key => {
            if (filters[key] !== undefined && filters[key] !== null && filters[key] !== "") {
                queryParams.append(key, filters[key]);
            }
        });

        const queryString = queryParams.toString();
        const endpoint = `/products${queryString ? `?${queryString}` : ""}`;

        return this.request(endpoint);
    }

    async getProductById(id) {
        return this.request(`/products/${id}`);
    }

    async getProductsByCategory(category, filters = {}) {
        const queryParams = new URLSearchParams(filters);
        const queryString = queryParams.toString();
        const endpoint = `/products/category/${encodeURIComponent(category)}${queryString ? `?${queryString}` : ""}`;

        return this.request(endpoint);
    }

    async getCategories() {
        return this.request("/products/categories");
    }

    async getFeaturedProducts() {
        return this.request("/products/featured");
    }

    async getNewArrivals() {
        return this.request("/products/new-arrivals");
    }

    async getBestSellers() {
        return this.request("/products/best-sellers");
    }

    async getFestiveSpecials() {
        return this.request("/products/festive-specials");
    }

    async getFeaturedTemplatesForLanding() {
        return this.request("/templates/featured-landing");
    }

    async getHomeHeroBanners() {
        return this.request("/banners/home-hero");
    }

    async getCategoryBanner(categorySlug) {
        return this.request(`/banners/category/${categorySlug}`);
    }

    async getInhousePublications() {
        return this.request("/products/inhouse-publications");
    }

    async getHotSelling() {
        return this.request("/products/hot-selling");
    }

    async getPublicSettings() {
        return this.request("/settings");
    }

    // Search products
    async searchProducts(query, filters = {}) {
        const queryParams = new URLSearchParams({
            search: query,
            ...filters,
        });

        return this.request(`/products?${queryParams.toString()}`);
    }

    // Cart API methods
    async getCart() {
        const token = localStorage.getItem("token");
        return this.request("/cart", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    }

    async addToCart(productId, quantity = 1, customization = {}) {
        const token = localStorage.getItem("token");
        return this.request("/cart/add", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                productId,
                quantity,
                customization,
            }),
        });
    }

    async updateCartItem(itemId, quantity) {
        const token = localStorage.getItem("token");
        return this.request(`/cart/update/${itemId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ quantity }),
        });
    }

    async removeFromCart(itemId) {
        const token = localStorage.getItem("token");
        return this.request(`/cart/remove/${itemId}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    }

    // Auth API methods
    async login(email, password) {
        return this.request("/auth/login", {
            method: "POST",
            body: JSON.stringify({ email, password }),
        });
    }

    async register(userData) {
        return this.request("/auth/register", {
            method: "POST",
            body: JSON.stringify(userData),
        });
    }

    async logout() {
        const token = localStorage.getItem("token");
        return this.request("/auth/logout", {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    }

    async verifyToken() {
        const token = localStorage.getItem("token");
        if (!token) {
            return { success: false };
        }

        return this.request("/auth/me", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    }

    async forgotPassword(email) {
        return this.request("/auth/forgot-password", {
            method: "POST",
            body: JSON.stringify({ email }),
        });
    }

    async resetPassword(token, password) {
        return this.request(`/auth/reset-password/${token}`, {
            method: "PATCH",
            body: JSON.stringify({ password }),
        });
    }

    async updateProfile(userData) {
        const token = localStorage.getItem("token");
        return this.request("/users/profile", {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(userData),
        });
    }

    // Order API methods
    async createOrder(orderData) {
        const token = localStorage.getItem("token");
        return this.request("/orders", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(orderData),
        });
    }

    // Payment API methods
    async createPaymentOrder(orderId, amount) {
        const token = localStorage.getItem("token");
        return this.request("/payment/create", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ orderId, amount }),
        });
    }

    // Buy Now API method - direct order creation bypassing cart
    async createDirectOrder(orderData) {
        const token = localStorage.getItem("token");
        if (!token || token === "null" || token === "undefined") {
            throw new Error("Authentication required. Please log in to place an order.");
        }

        const headers = {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
        };

        return this.request("/payment/create-order", {
            method: "POST",
            headers: headers,
            body: JSON.stringify(orderData),
        });
    }

    // Deprecated: Active payment gateway is CCAvenue. Retained as stubs for backward compatibility.
    async verifyPayment(_paymentData) {
        console.warn('verifyPayment called but CCAvenue uses server-to-server callbacks.');
        return { status: 'success' };
    }

    async handlePaymentFailure(_failureData) {
        console.warn('handlePaymentFailure called but CCAvenue uses server-to-server callbacks.');
        return { status: 'failed' };
    }

    async getUserOrders(page = 1, limit = 10, status = "") {
        const token = localStorage.getItem("token");
        const queryParams = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            ...(status && { status }),
        });

        return this.request(`/user/orders?${queryParams.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    }

    async getOrderById(orderId) {
        const token = localStorage.getItem("token");
        return this.request(`/orders/${orderId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    }

    async cancelOrder(orderId, reason = "") {
        const token = localStorage.getItem("token");
        return this.request(`/orders/${orderId}/cancel`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ reason }),
        });
    }
    async retryFulfillment(orderId) {
        const token = localStorage.getItem("token");
        return this.request(`/fulfillment/orders/${orderId}/retry`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    }

    // Cache management methods
    clearCache() {
        this.cache.clear();
        console.log("API cache cleared");
    }

    clearCacheForEndpoint(endpoint) {
        const cacheKey = this.getCacheKey(endpoint);
        this.cache.delete(cacheKey);
        console.log(`Cache cleared for: ${endpoint}`);
    }

    invalidateRelatedCache(endpoint, method) {
        // Clear cache for related endpoints after mutations
        const endpointPatterns = [
            '/admin/products',
            '/admin/users',
            '/admin/orders',
            '/admin/categories',
            '/admin/templates',
            '/admin/coupons',
            '/admin/dashboard',
            '/admin/banners',
        ];

        endpointPatterns.forEach(pattern => {
            if (endpoint.includes(pattern)) {
                // Clear all cache entries that start with this pattern
                const keysToDelete = [];
                this.cache.forEach((value, key) => {
                    if (key.includes(pattern)) {
                        keysToDelete.push(key);
                    }
                });
                keysToDelete.forEach(key => {
                    this.cache.delete(key);
                    console.log(`Cache invalidated for: ${key} (after ${method} on ${endpoint})`);
                });
            }
        });
    }

    // Template API methods
    async getTemplates(filters = {}) {
        const queryParams = new URLSearchParams();

        // Add filters to query params
        if (filters.category) queryParams.append("category", filters.category);
        if (filters.style) queryParams.append("style", filters.style);
        if (filters.search) queryParams.append("search", filters.search);
        if (filters.featured !== undefined) queryParams.append("featured", filters.featured);
        if (filters.free !== undefined) queryParams.append("free", filters.free);
        if (filters.productId) queryParams.append("productId", filters.productId);
        if (filters.page) queryParams.append("page", filters.page);
        if (filters.limit) queryParams.append("limit", filters.limit);
        if (filters.sortBy) queryParams.append("sortBy", filters.sortBy);
        if (filters.sortOrder) queryParams.append("sortOrder", filters.sortOrder);

        const endpoint = `/templates${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
        return this.request(endpoint);
    }

    async getTemplateById(id) {
        return this.request(`/templates/${id}`);
    }

    async validateCoupon(code, cartTotal) {
        const token = localStorage.getItem("token");
        return this.request("/coupons/validate", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ code, cartTotal }),
        });
    }

    async getFeaturedCoupons() {
        return this.request("/coupons/featured");
    }
}

// Create a dedicated auth service instance
export const authService = {
    async login(email, password) {
        return apiService.login(email, password);
    },

    async register(userData) {
        return apiService.register(userData);
    },

    async logout() {
        return apiService.logout();
    },

    async verifyToken() {
        return apiService.verifyToken();
    },

    async updateProfile(userData) {
        return apiService.updateProfile(userData);
    },

    async forgotPassword(email) {
        return apiService.forgotPassword(email);
    },

    async resetPassword(token, password) {
        return apiService.resetPassword(token, password);
    },
};

// Create admin service instance
export const adminService = {
    // Dashboard and analytics
    async getDashboardStats(period = "7d") {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/dashboard/stats?period=${period}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    // Product management
    async getAllProducts(filters = {}) {
        const token = localStorage.getItem("token");
        const queryParams = new URLSearchParams(filters);
        return apiService.request(`/admin/products?${queryParams.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async getProductFormData() {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/products/form-data", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async createProduct(productData) {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/products", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(productData),
        });
    },

    async createProductWithImages(formData) {
        const token = localStorage.getItem("token");

        // For FormData, we need to use fetch directly without setting Content-Type
        const response = await fetch(`${apiService.baseURL}/admin/products`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        // Invalidate cache after creating product
        apiService.invalidateRelatedCache('/admin/products', 'POST');
        return data;
    },

    async updateProduct(productId, productData) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/products/${productId}`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(productData),
        });
    },

    async updateProductWithImages(productId, formData) {
        const token = localStorage.getItem("token");

        // For FormData, we need to use fetch directly without setting Content-Type
        const response = await fetch(`${apiService.baseURL}/admin/products/${productId}`, {
            method: "PATCH",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        // Invalidate cache after updating product
        apiService.invalidateRelatedCache('/admin/products', 'PATCH');
        return data;
    },

    async deleteProduct(productId) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/products/${productId}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async uploadProductsExcel(productsData) {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/products/upload-excel", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ products: productsData }),
        });
    },

    // User management
    async getAllUsers(filters = {}) {
        const token = localStorage.getItem("token");
        const queryParams = new URLSearchParams(filters);
        return apiService.request(`/admin/users?${queryParams.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async getUserDetails(userId) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/users/${userId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async updateUserStatus(userId, status) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/users/${userId}/status`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ status }),
        });
    },

    async getUserStats() {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/users/stats", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    // Order management
    async getAllOrders(filters = {}) {
        const token = localStorage.getItem("token");
        const queryParams = new URLSearchParams(filters);
        return apiService.request(`/admin/orders?${queryParams.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async getOrderById(orderId) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/orders/${orderId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async updateOrderStatus(orderId, status) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/orders/${orderId}/status`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ status }),
        });
    },

    async updateOrderPaymentStatus(orderId, paymentStatus, notes) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/orders/${orderId}/payment-status`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ paymentStatus, notes }),
        });
    },

    async updateOrderDeliveryDate(orderId, expectedDeliveryDate) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/orders/${orderId}/delivery-date`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ expectedDeliveryDate }),
        });
    },

    async deleteOrder(orderId) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/orders/${orderId}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async getOrderStatuses() {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/orders/statuses", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async retryFulfillment(orderId) {
        return apiService.retryFulfillment(orderId);
    },

    // Coupon management
    async getAllCoupons(filters = {}) {
        const token = localStorage.getItem("token");
        const queryParams = new URLSearchParams(filters);
        return apiService.request(`/admin/coupons?${queryParams.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async createCoupon(couponData) {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/coupons", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(couponData),
        });
    },

    async updateCoupon(couponId, couponData) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/coupons/${couponId}`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(couponData),
        });
    },

    async deleteCoupon(couponId) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/coupons/${couponId}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    // Template management
    async getAllTemplates(filters = {}) {
        const token = localStorage.getItem("token");
        const queryParams = new URLSearchParams(filters);
        return apiService.request(`/admin/templates?${queryParams.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async createTemplate(templateData) {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/templates", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(templateData),
        });
    },

    async updateTemplate(templateId, templateData) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/templates/${templateId}`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(templateData),
        });
    },

    async deleteTemplate(templateId) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/templates/${templateId}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    // Thumbnail Upload for Templates (Cloudinary)
    async uploadTemplateThumbnail(templateId, formData) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/templates/${templateId}/upload/thumbnail`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });
    },

    // Analytics and Reports
    async getSalesReport(filters = {}) {
        const token = localStorage.getItem("token");
        const queryParams = new URLSearchParams(filters);
        return apiService.request(`/admin/reports/sales?${queryParams.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async getSalesAnalytics(period = "7d") {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/analytics/sales?period=${period}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async getProductAnalytics(filters = {}) {
        const token = localStorage.getItem("token");
        const queryParams = new URLSearchParams(filters);
        return apiService.request(`/admin/analytics/products?${queryParams.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async getProductPerformance(filters = {}) {
        const token = localStorage.getItem("token");
        const queryParams = new URLSearchParams(filters);
        return apiService.request(`/admin/analytics/product-performance?${queryParams.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async getRevenueAnalytics(period = "7d", groupBy = "day") {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/analytics/revenue?period=${period}&groupBy=${groupBy}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async getOrderAnalytics(period = "7d") {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/analytics/orders?period=${period}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    // Category management
    async getCategories() {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/categories", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async getActiveCategories() {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/categories/active", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async getCategoryUsage(categoryId) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/categories/${categoryId}/usage`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async createCategory(name, isActive = true, imageFile = null, subcategories = [], subcategoryImages = {}) {
        const token = localStorage.getItem("token");
        const formData = new FormData();
        formData.append("name", name);
        formData.append("isActive", String(isActive));

        if (subcategories && subcategories.length > 0) {
            formData.append("subcategories", JSON.stringify(subcategories));
        }

        if (imageFile) {
            formData.append("image", imageFile);
        }

        // Add subcategory images
        Object.keys(subcategoryImages).forEach(key => {
            formData.append(key, subcategoryImages[key]);
        });

        return apiService.request("/admin/categories", {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });
    },

    async updateCategory(categoryId, updates, imageFile = null, subcategoryImages = {}) {
        const token = localStorage.getItem("token");
        const formData = new FormData();

        if (updates.name !== undefined) {
            formData.append("name", updates.name);
        }
        if (updates.isActive !== undefined) {
            formData.append("isActive", String(updates.isActive));
        }
        if (updates.subcategories !== undefined) {
            formData.append("subcategories", JSON.stringify(updates.subcategories));
        }

        if (imageFile) {
            formData.append("image", imageFile);
        }

        // Add subcategory images
        Object.keys(subcategoryImages).forEach(key => {
            formData.append(key, subcategoryImages[key]);
        });

        return apiService.request(`/admin/categories/${categoryId}`, {
            method: "PATCH",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });
    },

    async deleteCategory(categoryId) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/categories/${categoryId}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async reorderCategories(order) {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/categories/reorder", {
            method: "PATCH",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ order }),
        });
    },

    // Banner management
    async getAllBanners(filters = {}) {
        const token = localStorage.getItem("token");
        const queryParams = new URLSearchParams(filters);
        return apiService.request(`/admin/banners?${queryParams.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async getBanner(bannerId) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/banners/${bannerId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async createBanner(bannerData, imageFile, mobileImageFile = null) {
        const token = localStorage.getItem("token");
        const formData = new FormData();

        formData.append("type", bannerData.type);
        if (bannerData.category) {
            formData.append("category", String(bannerData.category));
        }
        if (bannerData.headline) formData.append("headline", bannerData.headline);
        if (bannerData.subheadline) formData.append("subheadline", bannerData.subheadline);
        if (bannerData.buttonText) formData.append("buttonText", bannerData.buttonText);
        if (bannerData.buttonLink) formData.append("buttonLink", bannerData.buttonLink);
        if (bannerData.navLinks) {
            formData.append("navLinks", JSON.stringify(bannerData.navLinks));
        }
        if (bannerData.isActive !== undefined) {
            formData.append("isActive", String(bannerData.isActive));
        }

        if (imageFile) {
            formData.append("image", imageFile);
        }
        if (mobileImageFile) {
            formData.append("mobileImage", mobileImageFile);
        }

        const response = await fetch(`${apiService.baseURL}/admin/banners`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        apiService.invalidateRelatedCache('/admin/banners', 'POST');
        return data;
    },

    async updateBanner(bannerId, bannerData, imageFile = null, mobileImageFile = null) {
        const token = localStorage.getItem("token");
        const formData = new FormData();

        if (bannerData.headline !== undefined) formData.append("headline", bannerData.headline);
        if (bannerData.subheadline !== undefined) formData.append("subheadline", bannerData.subheadline);
        if (bannerData.buttonText !== undefined) formData.append("buttonText", bannerData.buttonText);
        if (bannerData.buttonLink !== undefined) formData.append("buttonLink", bannerData.buttonLink);
        if (bannerData.navLinks !== undefined) {
            formData.append("navLinks", JSON.stringify(bannerData.navLinks));
        }
        if (bannerData.isActive !== undefined) {
            formData.append("isActive", String(bannerData.isActive));
        }
        if (bannerData.category !== undefined && bannerData.category !== "") {
            formData.append("category", String(bannerData.category));
        }

        if (imageFile) {
            formData.append("image", imageFile);
        }
        if (mobileImageFile) {
            formData.append("mobileImage", mobileImageFile);
        }

        const response = await fetch(`${apiService.baseURL}/admin/banners/${bannerId}`, {
            method: "PATCH",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        apiService.invalidateRelatedCache('/admin/banners', 'PATCH');
        return data;
    },

    async deleteBanner(bannerId) {
        const token = localStorage.getItem("token");
        const result = await apiService.request(`/admin/banners/${bannerId}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        apiService.invalidateRelatedCache('/admin/banners', 'DELETE');
        return result;
    },

    async reorderBanners(bannerIds) {
        const token = localStorage.getItem("token");
        const result = await apiService.request("/admin/banners/reorder", {
            method: "PATCH",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ bannerIds }),
        });
        apiService.invalidateRelatedCache('/admin/banners', 'PATCH');
        return result;
    },

    // Festive Specials management
    async getFestiveSpecials() {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/festive-specials", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async addFestiveSpecial(productId) {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/festive-specials", {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ productId }),
        });
    },

    async removeFestiveSpecial(festiveSpecialId) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/festive-specials/${festiveSpecialId}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async reorderFestiveSpecials(order) {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/festive-specials/reorder", {
            method: "PATCH",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ order }),
        });
    },

    // Featured Templates management
    async getFeaturedTemplates() {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/featured-templates", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async addFeaturedTemplate(templateId) {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/featured-templates", {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ templateId }),
        });
    },

    async removeFeaturedTemplate(featuredTemplateId) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/featured-templates/${featuredTemplateId}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async reorderFeaturedTemplates(order) {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/featured-templates/reorder", {
            method: "PATCH",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ order }),
        });
    },

    // Inhouse Publications management
    async getInhousePublications() {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/inhouse-publications", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async addInhousePublication(productId) {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/inhouse-publications", {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ productId }),
        });
    },

    async removeInhousePublication(inhousePublicationId) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/inhouse-publications/${inhousePublicationId}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async reorderInhousePublications(order) {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/inhouse-publications/reorder", {
            method: "PATCH",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ order }),
        });
    },

    // Hot Selling management
    async getHotSelling() {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/hot-selling", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async addHotSelling(productId) {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/hot-selling", {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ productId }),
        });
    },

    async removeHotSelling(hotSellingId) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/hot-selling/${hotSellingId}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async reorderHotSelling(order) {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/hot-selling/reorder", {
            method: "PATCH",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ order }),
        });
    },

    // Site Settings management
    async getSiteSettings() {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/settings", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async updateSiteSettings(settings) {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/settings", {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(settings),
        });
    },

    // Email Template management
    async getAllEmailTemplates(filters = {}) {
        const token = localStorage.getItem("token");
        const queryParams = new URLSearchParams(filters);
        return apiService.request(`/admin/email-templates?${queryParams.toString()}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async getEmailTemplate(name) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/email-templates/${name}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async createEmailTemplate(templateData) {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/email-templates", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(templateData),
        });
    },

    async updateEmailTemplate(name, templateData) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/email-templates/${name}`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(templateData),
        });
    },

    async deleteEmailTemplate(name) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/email-templates/${name}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    },

    async previewEmailTemplate(name, testVariables) {
        const token = localStorage.getItem("token");
        return apiService.request(`/admin/email-templates/${name}/preview`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ variables: testVariables }),
        });
    },

    async sendCustomEmail(emailData) {
        const token = localStorage.getItem("token");
        return apiService.request("/admin/email-templates/send", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(emailData),
        });
    },
};

const apiService = new ApiService();
export default apiService;
